import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { getService } from "../../../reducers/service";
import { notify } from "../../../reducers/NotifierReducer";
import { setCircularLoading } from "../../../reducers/UiReducer";
import { FLSAOptions, RehireOptions } from "../../../utils/enums";
import { serviceNames } from "../../../utils/constants";
import { PATH_EMPLOYEE } from "../../../utils/routeConstants";
import {
  formatToTwoDecimals,
  mapDictionaryOptions,
} from "../../../utils/functions";

export const useEmployeeData = (id, reset) => {
  const dispatch = useDispatch();
  const userAgency = useSelector((state) => state.user.userAgency);
  const history = useHistory();
  const dictionary = useSelector((state) => state.dictionary.dictionary);

  const [personName, setPersonName] = useState(null);
  const [latestAssignmentData, setLatestAssignmentData] = useState({});
  const flsaStatusOptions = [
    { label: FLSAOptions.EXEMPT, value: FLSAOptions.EXEMPT },
    { label: FLSAOptions.NON_EXEMPT, value: FLSAOptions.NON_EXEMPT },
  ];
  const rehireOptions = [
    { label: RehireOptions.OK, value: RehireOptions.OK },
    { label: RehireOptions.NOT_OK, value: RehireOptions.NOT_OK },
  ];
  const [loaded, setLoaded] = useState(false);
  const [oldEmployeeNum, setOldEmployeeNum] = useState(null);

  const raceOptions = mapDictionaryOptions(dictionary.codeRace);
  const genderOptions = mapDictionaryOptions(dictionary.codeSex);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);
  const rankOptions = mapDictionaryOptions(dictionary.codePerRank);
  const countyOptions = mapDictionaryOptions(dictionary.codeCounties);
  const titlePositionOptions = mapDictionaryOptions(
    dictionary.codePerTitlePosition
  );
  const departmentOptions = mapDictionaryOptions(dictionary.codePerDepartment);
  const sectionOptions = mapDictionaryOptions(dictionary.codePerSection);
  const employmentOptions = mapDictionaryOptions(
    dictionary.codePerEmploymentStatuses
  );
  const educationOptions = mapDictionaryOptions(dictionary.codePerEducation);
  const stateOptions = mapDictionaryOptions(dictionary.codeStates);
  const maritalOptions = mapDictionaryOptions(dictionary.codeMaritalStatus);
  const bloodTypeOptions = mapDictionaryOptions(dictionary.codeBloodType);
  const cityOptions = useMemo(() => {
    return (dictionary.codeCities || []).map((g) => ({
      label: g.State ? `${g.CityDescription}, ${g.State}` : g.CityDescription,
      value: g.CityDescription,
      state: g.State,
    }));
  }, [dictionary.codeCities]);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        dispatch(setCircularLoading(true));
        setLoaded(false);

        if (id) {
          const service = getService(serviceNames.personnel);
          const result = await service.get(id);
          if (isMounted && result) {
            reset({
              id: result.id || null,
              firstName: result.firstName || "",
              lastName: result.lastName || "",
              middleName: result.middleName || "",
              suffix: result.suffix || "",
              dob: result.dob ? moment(result.dob).format("MM/DD/YYYY") : "",
              ssn: result.ssn || "",
              email: result.email || "",
              gender: result.gender || "",
              employeeNum: result.employeeNum || null,
              phoneNo: result.phoneNo || "",
              workPhoneNum: result.workPhoneNum || "",
              cellNo: result.cellNo || "",
              pagerNo: result.pagerNo || "",
              dl: result.dl || "",
              dlState: result.dlState || "",
              dlExpire: result.dlExpire
                ? moment(result.dlExpire).format("MM/DD/YYYY")
                : "",
              race: result.race || "",
              status: result.status || "",
              address: result.address || "",
              rCity: result.rCity || "",
              rState: result.rState || "",
              rZip: result.rZip || "",
              mAddress: result.mAddress || "",
              mCity: result.mCity || "",
              mState: result.mState || "",
              mZip: result.mZip || "",
              maritalStatus: result.maritalStatus || "",
              spouseName: result.spouseName || "",
              spouseSSN: result.spouseSSN || "",
              spouseEmployer: result.spouseEmployer || "",
              spousePhoneNumber: result.spousePhoneNumber || "",
              emergencyContact: result.emergencyContact || "",
              ecHomePhone: result.ecHomePhone || "",
              ecCellNo: result.ecCellNo || "",
              contactAddress: result.contactAddress || "",
              contactCity: result.contactCity || "",
              contactState: result.contactState || "",
              contactZip: result.contactZip || "",
              medicalNotes: result.medicalNotes || "",
              bloodType: result.bloodType || "",
              relationship: result.relationship || "",
              positionNum: result.positionNum || "",
              masterCostCenter: result.masterCostCenter || "",
              salary: result.salary ? formatToTwoDecimals(result.salary) : "",
              annualSalary: result.annualSalary
                ? formatToTwoDecimals(result.annualSalary)
                : "",
              titlePosition: result.titlePosition || "",
              rank: result.rank || "",
              classCode: result.classCode || "",
              grade: result.grade || "",
              personnelNum: result.personnelNum || "",
              personnelArea: result.personnelArea || "",
              subArea: result.subArea || "",
              orgUnit: result.orgUnit || "",
              contract: result.contract || "",
              positionStartDate: result.positionStartDate
                ? moment(result.positionStartDate).format("MM/DD/YYYY")
                : "",
              positionEndDate: result.positionEndDate
                ? moment(result.positionEndDate).format("MM/DD/YYYY")
                : "",
              dateLwopMilitary: result.dateLwopMilitary
                ? moment(result.dateLwopMilitary).format("MM/DD/YYYY")
                : "",
              dateReturnedPayroll: result.dateReturnedPayroll
                ? moment(result.dateReturnedPayroll).format("MM/DD/YYYY")
                : "",
              perfRevDate: result.perfRevDate
                ? moment(result.perfRevDate).format("MM/DD/YYYY")
                : "",
              careerServiceDate: result.careerServiceDate
                ? moment(result.careerServiceDate).format("MM/DD/YYYY")
                : "",
              leaveAccDate: result.leaveAccDate
                ? moment(result.leaveAccDate).format("MM/DD/YYYY")
                : "",
              careerServicePayment: result.careerServicePayment
                ? formatToTwoDecimals(result.careerServicePayment)
                : "",
              orderNum: result.orderNum || "",
              percentage: result.percentage || "",
              internalCC: result.internalCC || "",
              wbsElement: result.wbsElement || "",
              wbscc: result.wbscc || "",
              otherCC: result.otherCC || "",
              orgChart: result.orgChart || false,
              flsaStatus:
                result.flsaStatus === true
                  ? "Non Exempt"
                  : result.flsaStatus === false
                  ? "Exempt"
                  : null,
              cgdg: result.cgdg || false,
              clip: result.clip || false,
              commissionedDate: result.commissionedDate
                ? moment(result.commissionedDate).format("MM/DD/YYYY")
                : "",
              trooperDate: result.trooperDate
                ? moment(result.trooperDate).format("MM/DD/YYYY")
                : "",
              latestHireDate: result.latestHireDate
                ? moment(result.latestHireDate).format("MM/DD/YYYY")
                : "",
              hireDate: result.hireDate
                ? moment(result.hireDate).format("MM/DD/YYYY")
                : "",
              badgeNum: result.badgeNum || "",
              department: result.department || "",
              departmentDate: result.departmentDate
                ? moment(result.departmentDate).format("MM/DD/YYYY")
                : "",
              section: result.section || "",
              sectionDate: result.sectionDate
                ? moment(result.sectionDate).format("MM/DD/YYYY")
                : "",
              residentialCounty: result.residentialCounty || "",
              assignedCounty: result.assignedCounty || "",
              education: result.education || "",
              notes: result.notes || "",
              militaryNotes: result.militaryNotes || "",
              dropDate: result.dropDate
                ? moment(result.dropDate).format("MM/DD/YYYY")
                : "",
              retireDate: result.retireDate
                ? moment(result.retireDate).format("MM/DD/YYYY")
                : "",
              separationDate: result.separationDate
                ? moment(result.separationDate).format("MM/DD/YYYY")
                : "",
              separationCode: result.separationCode || "",
              separationComments: result.separationComments || "",
              rehire:
                result.rehire === 1
                  ? "OK to rehire"
                  : result.rehire === 2
                  ? "Do not rehire"
                  : null,
            });
            const personName = `${result.firstName}${
              result.middleName ? ` ${result.middleName.charAt(0)}.` : ""
            } ${result.lastName}`;

            setLatestAssignmentData({
              trooperDate: result.trooperDate
                ? moment(result.trooperDate).format("MM/DD/YYYY")
                : "",
              department: result.department || "",
              departmentDate: result.departmentDate
                ? moment(result.departmentDate).format("MM/DD/YYYY")
                : "",
              section: result.section || "",
              sectionDate: result.sectionDate
                ? moment(result.sectionDate).format("MM/DD/YYYY")
                : "",
              titlePosition: result.titlePosition || "",
              rank: result.rank || "",
            });

            setPersonName(personName);
            setOldEmployeeNum(result.employeeNum || null);
          }
        }
      } catch (error) {
        if (isMounted) {
          dispatch(notify("Failed to fetch employee", "error"));
          history.push(PATH_EMPLOYEE);
        }
      } finally {
        dispatch(setCircularLoading(false));
        setLoaded(true);
      }
    };

    init();
    return () => {
      isMounted = false;
    };
  }, [id, dispatch, reset, userAgency]);

  return {
    personName,
    genderOptions,
    raceOptions,
    employmentOptions,
    stateOptions,
    maritalOptions,
    bloodTypeOptions,
    flsaStatusOptions,
    tagTypeOptions,
    countyOptions,
    rankOptions,
    rehireOptions,
    titlePositionOptions,
    departmentOptions,
    sectionOptions,
    educationOptions,
    suffixOptions,
    cityOptions,
    oldEmployeeNum,
    latestAssignmentData,
    loaded,
  };
};
